import { MirrorsGame } from './MirrorsGame.js';
import { MirrorsVortex } from './MirrorsVortex.js';
import { Grid } from '../../core/Grid.js';
import { Position, Direction } from '../../core/GridGeometry.js';
import { LaserPath, MirrorType } from './MirrorsGame.js';
import { GridRenderer, CanvasSeg } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, staggerDelay, Motion, MOTION } from '../../rendering/juice.js';

export interface MirrorsRenderConfig extends Partial<RenderConfig> {
    mirrorColor?: string;
    unhitMirrorColor?: string;
    laserColor?: string;
    reverseLaserColor?: string;
    laserWidth?: number;
    mirrorWidth?: number;
}

const MIRRORS_DEFAULTS = {
    mirrorColor: COLORS.teal,
    unhitMirrorColor: COLORS.amber,
    laserColor: COLORS.teal,
    reverseLaserColor: COLORS.amber,
    laserWidth: 2,
    mirrorWidth: 5
};

interface BeamAnalysis {
    lastValidSegmentIdx: number;        // segment where last in-sequence digit was crossed (-1 if none)
    firstViolationSegmentIdx: number;  // segment where first violation (wrong order or double-hit) occurs (-1 if none)
    validDigits: Set<number>;          // digits crossed in correct sequential order
}

interface HintBeamAnalysis {
    lastValidSegmentIdx: number;       // segment where last valid reverse-order digit was crossed (-1 if none)
    validDigits: Set<number>;          // digits crossed in correct reverse order
    invalidDigits: Set<number>;        // digits crossed but NOT in correct reverse order
}

// ── Mirror animation (docs/juice.md) ──────────────────────────────────
//
// A mirror is one physical object with a stroke direction, not two unrelated
// diagonals. The canonical form is `/`, stroked from its bottom-left end to its
// top-right end; `\` is that same object rotated a quarter-turn clockwise, which
// carries the stroke start round to the top-left. So the three transitions are
// all the same object: it gets *drawn*, it *turns*, it gets *un-drawn*.

/** Rotation, in radians, that turns the canonical `/` stroke into this mirror. */
const MIRROR_ANGLE: Record<MirrorType, number> = { '/': 0, '\\': Math.PI / 2 };

type MirrorTweenKind = 'draw' | 'rotate' | 'erase';

/** The three transitions map straight onto the shared motion roles: a mirror
 *  arrives, turns in place, or leaves. No Mirrors-specific curves or timings. */
const MIRROR_MOTION: Record<MirrorTweenKind, Motion> = {
    draw: MOTION.enter,
    rotate: MOTION.change,
    erase: MOTION.exit,
};

interface MirrorTween {
    kind: MirrorTweenKind;
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** The mirror being drawn or erased; for a rotate, the one being turned *from*. */
    type: MirrorType;
    /** Rotate only: +1 for a clockwise quarter-turn, -1 for anticlockwise. */
    spin: number;
    /** Erase only: the colour the mirror had on the last frame it existed. */
    color: string;
}

export class MirrorsRenderer extends GridRenderer {
    private mirrorsConfig: typeof MIRRORS_DEFAULTS;
    private gateMode: boolean = false;
    private hintBeamEnabled: boolean = false;

    // Mirror tween state, all renderer-local: the board is still drawn purely
    // from game state, with these as a transient overlay on the way there.
    private tweens = new TweenScheduler();
    private mirrorTweens = new Map<string, MirrorTween>();
    private mirrorState = new Map<string, MirrorType>();   // what the last frame drew
    private mirrorColors = new Map<string, string>();      // last colour, for erases
    private mirrorsSynced: boolean = false;                // first frame seeds, never animates
    /** The cell whose beam crossing is replaying, and when it started. One at a
     *  time: this is feedback for a click, not the board re-tracing itself. */
    private beamPlay: { key: string; cell: Position; start: number } | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: MirrorsGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: MirrorsRenderConfig = {}) {
        super(canvas, grid, config);
        this.mirrorsConfig = { ...MIRRORS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable mirror tweening (see `TweenScheduler`). Drops anything in flight,
     *  so the next render draws every mirror at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.mirrorTweens.clear();
        this.beamPlay = null;
    }

    /** Toggle the amber backward hint beam (off by default; the exit marker stays). */
    setHintBeamEnabled(enabled: boolean): void {
        this.hintBeamEnabled = enabled;
    }

    render(game: MirrorsGame): void {
        const grid = game.getGrid();
        this.lastGame = game;
        this.animating = false;
        // Before the beams: the diff is what starts a cell's replay, and the beams
        // are the first thing drawn.
        this.syncMirrorTweens(game.getMirrors());
        const won = game.checkWin();
        const mainPath = game.traceLaser();
        const mainAnalysis = this.analyzeMainBeam(grid, mainPath);

        // On win, force the entire beam to be solid
        if (won) {
            mainAnalysis.lastValidSegmentIdx = mainPath.segments.length - 1;
        }

        this.clear();
        this.drawGrid();

        // The cell the player just clicked replays the beam's way through it. The
        // beam is drawn as long runs across the board, so the cell is clipped out
        // of them and its own crossing drawn separately (docs/juice.md).
        const replay = this.beamReplay();
        const mainColor = (segIdx: number) =>
            mainAnalysis.firstViolationSegmentIdx >= 0 && segIdx >= mainAnalysis.firstViolationSegmentIdx
                ? COLORS.red : COLORS.teal;

        let reversePath: LaserPath | null = null;
        let hintAnalysis: HintBeamAnalysis | null = null;
        if (this.hintBeamEnabled && !mainPath.exitPosition) {
            reversePath = game.traceReverseLaser();
            hintAnalysis = this.analyzeHintBeam(grid, reversePath);
        }

        if (replay) this.ctx.save();
        if (replay) this.clipOutCells([replay.cell]);
        if (reversePath && hintAnalysis) this.drawReverseLaser(reversePath, hintAnalysis);
        this.drawLaser(mainPath, mainAnalysis);
        if (replay) this.ctx.restore();

        if (replay) {
            // Drawn after the clipped runs so the growing crossing sits on top of
            // the round caps the clip leaves at the cell's edges. The hint beam runs
            // backward from the exit, so it enters the grid heading west.
            if (reversePath && hintAnalysis) {
                this.drawBeamCrossing(reversePath, replay.cell, replay.p, 'W',
                    () => this.mirrorsConfig.reverseLaserColor,
                    idx => idx > hintAnalysis!.lastValidSegmentIdx);
            }
            this.drawBeamCrossing(mainPath, replay.cell, replay.p, 'E', mainColor,
                idx => idx > mainAnalysis.lastValidSegmentIdx);
        }

        // Triangles sit astride cell edges, so they're drawn outside the clip —
        // one at a neighbouring digit would otherwise be sliced in half.
        if (reversePath) {
            this.drawBeamTriangles(game, reversePath, this.mirrorsConfig.reverseLaserColor, true);
        }
        this.drawBeamTriangles(game, mainPath, mainColor);

        this.drawEntryExitMarkers(game);
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
        }
        this.drawMirrors(game, undefined, this.hintBeamEnabled);
        this.drawWarnings(game);
        this.drawDigits(grid);
        this.drawDigitBadges(game, mainAnalysis, hintAnalysis);

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Analysis ──────────────────────────────────────────────────────

    protected analyzeMainBeam(grid: Grid, path: LaserPath): BeamAnalysis {
        const seenDigits = new Set<number>();
        const digitFirstSegment = new Map<number, number>();
        let expectedDigit = grid.minDigit;
        let lastValidDigit = -1;
        let firstViolationSegmentIdx = -1;
        const validDigits = new Set<number>();
        let sequenceBroken = false;

        for (let i = 0; i < path.segments.length; i++) {
            const digit = grid.getDigitAt(path.segments[i].from);
            if (digit === null) continue;

            if (seenDigits.has(digit)) {
                // Double-hit violation
                if (firstViolationSegmentIdx === -1) firstViolationSegmentIdx = i;
            } else {
                seenDigits.add(digit);
                digitFirstSegment.set(digit, i);
                if (!sequenceBroken && digit === expectedDigit) {
                    lastValidDigit = digit;
                    validDigits.add(digit);
                    expectedDigit++;
                } else if (!sequenceBroken) {
                    // Wrong-order violation
                    sequenceBroken = true;
                    if (firstViolationSegmentIdx === -1) firstViolationSegmentIdx = i;
                }
            }
        }

        const lastValidSegmentIdx = lastValidDigit >= grid.minDigit
            ? digitFirstSegment.get(lastValidDigit) ?? -1
            : -1;

        return { lastValidSegmentIdx, firstViolationSegmentIdx, validDigits };
    }

    protected analyzeHintBeam(grid: Grid, path: LaserPath): HintBeamAnalysis {
        const seenDigits = new Set<number>();
        const digitFirstSegment = new Map<number, number>();
        let expectedDigit = grid.maxDigit;
        let lastValidDigit = -1;

        for (let i = 0; i < path.segments.length; i++) {
            const digit = grid.getDigitAt(path.segments[i].from);
            if (digit === null) continue;

            if (!seenDigits.has(digit)) {
                seenDigits.add(digit);
                digitFirstSegment.set(digit, i);
            }
        }

        const validDigits = new Set<number>();
        const invalidDigits = new Set<number>();
        let sequenceBroken = false;
        for (const digit of path.digitsHit) {
            if (!sequenceBroken && digit === expectedDigit) {
                lastValidDigit = digit;
                validDigits.add(digit);
                expectedDigit--;
            } else {
                sequenceBroken = true;
                invalidDigits.add(digit);
            }
        }

        const lastValidSegmentIdx = lastValidDigit >= grid.minDigit
            ? digitFirstSegment.get(lastValidDigit) ?? -1
            : -1;

        return { lastValidSegmentIdx, validDigits, invalidDigits };
    }

    // ── Entry/Exit Markers ────────────────────────────────────────────

    protected drawEntryExitMarkers(game: MirrorsGame): void {
        // Entry marker (top-left cell, left edge) — always teal
        this.drawRimMarker({
            position: { row: 0, col: 0 },
            color: COLORS.teal,
            entry: true,
            edge: 'left'
        });

        // Exit marker: amber default, teal on win, red if beam reached exit without winning
        const won = game.checkWin();
        const path = game.traceLaser();
        const reachedExit = path.exitPosition !== null;
        const exitColor = won ? COLORS.teal : reachedExit ? COLORS.red : COLORS.amber;

        this.drawRimMarker({
            position: { row: this.grid.size - 1, col: this.grid.size - 1 },
            color: exitColor,
            entry: false,
            edge: 'right'
        });
    }

    // ── Phased Beam Drawing ───────────────────────────────────────────

    private drawBeamPhase(
        path: LaserPath,
        fromIdx: number,
        toIdx: number,
        color: string,
        dashed: boolean,
        entryPoint?: { x: number; y: number }
    ): void {
        if (fromIdx >= toIdx) return;

        const { padding, cellSize } = this.config;

        // Build the beam as straight pieces, coalescing collinear cell-steps into a
        // single run (so dashes/corners stay clean) and breaking at teleports.
        const pieces: CanvasSeg[] = [];
        const addPiece = (from: { x: number; y: number }, to: { x: number; y: number }) => {
            if (Math.abs(from.x - to.x) < 0.01 && Math.abs(from.y - to.y) < 0.01) return;
            const last = pieces[pieces.length - 1];
            const contiguous = last &&
                Math.abs(last.to.x - from.x) < 0.01 && Math.abs(last.to.y - from.y) < 0.01;
            const collinear = last &&
                ((last.from.y === last.to.y && from.y === to.y) ||
                 (last.from.x === last.to.x && from.x === to.x));
            if (contiguous && collinear) last.to = { ...to };
            else pieces.push({ from: { ...from }, to: { ...to } });
        };

        let cursor = entryPoint ?? this.getCellCenter(path.segments[fromIdx].from);

        for (let i = fromIdx; i < toIdx; i++) {
            const segment = path.segments[i];
            const fromCenter = this.getCellCenter(segment.from);

            if (segment.teleport) {
                addPiece(cursor, fromCenter);
                cursor = this.getCellCenter(segment.to);  // jump, no line drawn
                continue;
            }

            const end = { ...this.getCellCenter(segment.to) };
            if (segment.to.row < 0) end.y = padding;
            else if (segment.to.row >= this.grid.size) end.y = padding + this.grid.size * cellSize;
            if (segment.to.col < 0) end.x = padding;
            else if (segment.to.col >= this.grid.size) end.x = padding + this.grid.size * cellSize;

            addPiece(cursor, fromCenter);
            addPiece(fromCenter, end);
            cursor = end;
        }

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.mirrorsConfig.laserWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash(dashed ? [6, 4] : []);

        // Gap the beam where it passes over a digit; the edge triangles mark passage.
        for (const seg of this.clipSegmentsAtDigits(pieces)) {
            this.ctx.beginPath();
            this.ctx.moveTo(seg.from.x, seg.from.y);
            this.ctx.lineTo(seg.to.x, seg.to.y);
            this.ctx.stroke();
        }
        this.ctx.setLineDash([]);
    }

    protected drawLaser(path: LaserPath, analysis: BeamAnalysis): void {
        if (path.segments.length === 0) return;

        const { padding, cellSize } = this.config;
        const totalSegments = path.segments.length;

        // Entry point (outside grid to first cell)
        const entryPoint = { x: padding - 10, y: padding + cellSize / 2 };

        // Phase boundaries from two axes: solid/dashed and teal/red
        const solidEndIdx = analysis.lastValidSegmentIdx + 1; // segments >= this are dashed
        const redStartIdx = analysis.firstViolationSegmentIdx; // segments >= this are red

        const boundaries = new Set<number>([0, totalSegments]);
        if (solidEndIdx > 0 && solidEndIdx < totalSegments) boundaries.add(solidEndIdx);
        if (redStartIdx >= 0 && redStartIdx < totalSegments) boundaries.add(redStartIdx);

        const sorted = [...boundaries].sort((a, b) => a - b);

        for (let b = 0; b < sorted.length - 1; b++) {
            const start = sorted[b];
            const end = sorted[b + 1];

            const dashed = start >= solidEndIdx;
            const isRed = redStartIdx >= 0 && start >= redStartIdx;
            const color = isRed ? COLORS.red : COLORS.teal;

            this.drawBeamPhase(
                path, start, end, color, dashed,
                start === 0 ? entryPoint : undefined
            );
        }

        // Draw exit extension if laser exits at correct position
        if (path.exitPosition &&
            path.exitPosition.row === this.grid.size - 1 &&
            path.exitPosition.col === this.grid.size - 1) {
            const exitX = padding + this.grid.size * cellSize;
            const exitY = padding + (this.grid.size - 0.5) * cellSize;

            // Color matches what the beam was at the end
            const lastColor = (redStartIdx >= 0 && totalSegments - 1 >= redStartIdx)
                ? COLORS.red : COLORS.teal;
            const lastDashed = totalSegments - 1 >= solidEndIdx;

            this.ctx.strokeStyle = lastColor;
            this.ctx.lineWidth = this.mirrorsConfig.laserWidth;
            this.ctx.setLineDash(lastDashed ? [6, 4] : []);
            this.ctx.beginPath();
            this.ctx.moveTo(exitX, exitY);
            this.ctx.lineTo(exitX + 15, exitY);
            this.ctx.stroke();
            this.ctx.setLineDash([]);
        }
    }

    protected drawReverseLaser(path: LaserPath, analysis: HintBeamAnalysis): void {
        if (path.segments.length === 0) return;

        const { padding, cellSize } = this.config;
        const totalSegments = path.segments.length;
        const color = this.mirrorsConfig.reverseLaserColor;

        // Entry point for hint beam (bottom-right edge)
        const entryPoint = {
            x: padding + this.grid.size * cellSize,
            y: padding + (this.grid.size - 0.5) * cellSize
        };

        const solidEndIdx = analysis.lastValidSegmentIdx + 1;

        // Phase 1: solid
        if (solidEndIdx > 0) {
            this.drawBeamPhase(path, 0, Math.min(solidEndIdx, totalSegments),
                color, false, entryPoint);
        }

        // Phase 2: dashed
        if (solidEndIdx < totalSegments) {
            this.drawBeamPhase(path, Math.max(solidEndIdx, 0), totalSegments,
                color, true,
                solidEndIdx <= 0 ? entryPoint : undefined);
        }
    }

    // ── Cell replay ───────────────────────────────────────────────────

    /** The cell replaying its beam crossing this frame, and how far in it is. */
    private beamReplay(): { cell: Position; p: number } | null {
        if (!this.beamPlay) return null;
        const { value, done } = this.tweens.tween(this.beamPlay.start, MOTION.replay);
        if (done) {
            this.beamPlay = null;
            return null;
        }
        this.animating = true;
        return { cell: this.beamPlay.cell, p: value };
    }

    /**
     * The beam's crossing of one cell, `p` of the way through: in from the edge it
     * enters to the mirror at the centre, then out by the edge it leaves. A cell can
     * be crossed more than once — each crossing plays.
     */
    private drawBeamCrossing(
        path: LaserPath, cell: Position, p: number, entryDir: Direction,
        colorAt: (segIdx: number) => string,
        dashedAt: (segIdx: number) => boolean
    ): void {
        this.ctx.lineWidth = this.mirrorsConfig.laserWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        for (let i = 0; i < path.segments.length; i++) {
            const seg = path.segments[i];
            if (seg.teleport) continue;
            if (seg.from.row !== cell.row || seg.from.col !== cell.col) continue;

            // The beam arrives on the previous segment's heading. Before the first
            // segment — or straight out of a gate — it is entering rather than
            // travelling, so it arrives on the heading it entered with.
            const prev = i > 0 ? path.segments[i - 1] : null;
            const inDir = prev && !prev.teleport ? prev.direction : entryDir;

            this.ctx.strokeStyle = colorAt(i);
            this.ctx.setLineDash(dashedAt(i) ? [6, 4] : []);
            for (const [a, b] of this.crossingHalves(cell, inDir, seg.direction, p)) {
                this.ctx.beginPath();
                this.ctx.moveTo(a.x, a.y);
                this.ctx.lineTo(b.x, b.y);
                this.ctx.stroke();
            }
        }
        this.ctx.setLineDash([]);
    }

    // ── Beam Triangles ────────────────────────────────────────────────

    protected drawBeamTriangles(
        game: MirrorsGame, path: LaserPath,
        colorOrFn: string | ((segIdx: number) => string),
        reverse: boolean = false
    ): void {
        const grid = game.getGrid();
        const isVortex = game instanceof MirrorsVortex;

        const isSpecial = (pos: Position): boolean =>
            grid.hasDigitAt(pos) ||
            (isVortex && (game as MirrorsVortex).isActiveGate(pos));

        const getColor = typeof colorOrFn === 'string'
            ? () => colorOrFn
            : colorOrFn;

        for (let segIdx = 0; segIdx < path.segments.length; segIdx++) {
            const segment = path.segments[segIdx];
            if (segment.teleport) continue;

            const toInside = segment.to.row >= 0 && segment.to.row < grid.size &&
                segment.to.col >= 0 && segment.to.col < grid.size;
            if (!toInside) continue;

            const color = getColor(segIdx);

            if (isSpecial(segment.from)) {
                this.drawBeamEdgeTriangle(segment.from, segment.direction, color, reverse, reverse);
            }
            if (isSpecial(segment.to)) {
                this.drawBeamEdgeTriangle(segment.from, segment.direction, color, !reverse, reverse);
            }
        }
    }

    // Canonical triangle points "down" (angle 0 = south).
    // Rotation maps south to the beam's travel direction.
    // flipAngle reverses the triangle for the hint beam.
    private drawBeamEdgeTriangle(
        from: Position, direction: Direction, color: string,
        inbound: boolean, flipAngle: boolean = false
    ): void {
        const { padding, cellSize } = this.config;
        const center = this.getCellCenter(from);

        let x: number, y: number;
        let angle: number;

        switch (direction) {
            case 'S':
                x = center.x;
                y = padding + (from.row + 1) * cellSize;
                angle = 0;
                break;
            case 'N':
                x = center.x;
                y = padding + from.row * cellSize;
                angle = Math.PI;
                break;
            case 'E':
                x = padding + (from.col + 1) * cellSize;
                y = center.y;
                angle = -Math.PI / 2;
                break;
            case 'W':
                x = padding + from.col * cellSize;
                y = center.y;
                angle = Math.PI / 2;
                break;
        }

        if (flipAngle) angle += Math.PI;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (inbound) {
            this.ctx.moveTo(-5, 1);
            this.ctx.lineTo(5, 1);
            this.ctx.lineTo(0, 9);
        } else {
            this.ctx.moveTo(-5, -9);
            this.ctx.lineTo(5, -9);
            this.ctx.lineTo(0, -1);
        }
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    // ── Mirrors ───────────────────────────────────────────────────────

    /** Scoring overlay: redraw the mirrors (primary) in amber. */
    protected drawScoringPrimary(game: MirrorsGame): void {
        this.drawMirrors(game, COLORS.amber);
    }

    protected drawMirrors(game: MirrorsGame, colorOverride?: string, includeReverse: boolean = true): void {
        const mirrors = game.getMirrors();

        // The scoring overlay redraws the mirrors in a flat colour on a frozen
        // board: it reads state, it doesn't advance it. (The diff itself already
        // ran, at the top of render.)
        const live = colorOverride === undefined;

        // Collect positions hit by either beam. The hint beam only counts toward
        // mirror coloring when it is actually shown.
        const hitPositions = new Set<string>();
        const mainPath = game.traceLaser();

        for (const segment of mainPath.segments) {
            hitPositions.add(`${segment.from.row},${segment.from.col}`);
        }
        if (includeReverse) {
            const reversePath = game.traceReverseLaser();
            for (const segment of reversePath.segments) {
                hitPositions.add(`${segment.from.row},${segment.from.col}`);
            }
        }

        const now = this.tweens.now();
        let animating = false;

        for (const [key, mirror] of mirrors) {
            const [row, col] = key.split(',').map(Number);
            const isHit = hitPositions.has(key);
            const color = colorOverride ??
                (isHit ? this.mirrorsConfig.mirrorColor : this.mirrorsConfig.unhitMirrorColor);
            if (live) this.mirrorColors.set(key, color);

            const tween = live ? this.mirrorTweens.get(key) : undefined;
            const shape = tween
                ? this.tweenShape(tween, now)
                : { angle: MIRROR_ANGLE[mirror], extent: 1, done: true };
            if (tween && shape.done) this.mirrorTweens.delete(key);
            else if (tween) animating = true;

            this.drawMirrorStroke({ row, col }, shape.angle, shape.extent, color);
        }

        // Erasing mirrors are already gone from game state — they only exist here,
        // un-drawing themselves in the colour they wore when they were removed.
        for (const [key, tween] of live ? this.mirrorTweens : []) {
            if (tween.kind !== 'erase' || mirrors.has(key)) continue;
            const [row, col] = key.split(',').map(Number);
            const shape = this.tweenShape(tween, now);
            if (shape.done) {
                this.mirrorTweens.delete(key);
                this.mirrorColors.delete(key);
                continue;
            }
            animating = true;
            this.drawMirrorStroke({ row, col }, shape.angle, shape.extent, tween.color);
        }

        if (animating) this.animating = true;
    }

    /**
     * Diff the board against the last frame and start a tween per changed cell:
     * placed mirrors draw themselves in, a swapped one turns a quarter-circle,
     * a removed one un-draws. A bulk change (reset, restoring a best solution)
     * fans out from the entry corner along the beam's direction of travel rather
     * than snapping as one block.
     */
    private syncMirrorTweens(mirrors: Map<string, MirrorType>): void {
        if (!this.tweens.isEnabled()) {
            this.mirrorTweens.clear();
            this.snapshotMirrors(mirrors);
            return;
        }
        // First frame on this board: adopt it as-is. Without this, opening a grid
        // with a saved solution would replay the whole thing.
        if (!this.mirrorsSynced) {
            this.snapshotMirrors(mirrors);
            this.mirrorsSynced = true;
            return;
        }

        const changed: Array<{ key: string; prev?: MirrorType; next?: MirrorType }> = [];
        for (const [key, next] of mirrors) {
            const prev = this.mirrorState.get(key);
            if (prev !== next) changed.push({ key, prev, next });
        }
        for (const [key, prev] of this.mirrorState) {
            if (!mirrors.has(key)) changed.push({ key, prev });
        }
        if (changed.length === 0) return;

        // Fan-out order: distance from the top-left entry, i.e. the way the laser
        // travels, so a whole solution appears to be laid down along the beam.
        const rank = (key: string) => {
            const [row, col] = key.split(',').map(Number);
            return (row + col) * this.grid.size + row;
        };
        changed.sort((a, b) => rank(a.key) - rank(b.key));

        const now = this.tweens.now();
        // One cell changing is a click, and the beam replays its way through it. A
        // bulk change (Reset, Restore best) isn't a question about any one cell.
        if (changed.length === 1) {
            const [row, col] = changed[0].key.split(',').map(Number);
            this.beamPlay = { key: changed[0].key, cell: { row, col }, start: now };
        } else {
            this.beamPlay = null;
        }

        changed.forEach(({ key, prev, next }, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (next === undefined) {
                this.mirrorTweens.set(key, {
                    kind: 'erase', start, type: prev!, spin: 0,
                    color: this.mirrorColors.get(key) ?? this.mirrorsConfig.unhitMirrorColor
                });
            } else if (prev === undefined) {
                this.mirrorTweens.set(key, { kind: 'draw', start, type: next, spin: 0, color: '' });
            } else {
                // `/`→`\` is the forward (left-click) step of the cycle, so it turns
                // clockwise; `\`→`/` is the backward step and turns the other way.
                this.mirrorTweens.set(key, {
                    kind: 'rotate', start, type: prev, spin: prev === '/' ? 1 : -1, color: ''
                });
            }
        });

        this.snapshotMirrors(mirrors);
    }

    private snapshotMirrors(mirrors: Map<string, MirrorType>): void {
        this.mirrorState = new Map(mirrors);
    }

    /** Where a tween has the stroke this frame: its rotation, and how much of it
     *  is on the board (0 = nothing, 1 = the full diagonal). */
    private tweenShape(tween: MirrorTween, now: number): { angle: number; extent: number; done: boolean } {
        const base = MIRROR_ANGLE[tween.type];
        const { value, done } = this.tweens.tween(tween.start, MIRROR_MOTION[tween.kind], now);
        switch (tween.kind) {
            case 'draw':
                return { angle: base, extent: value, done };
            case 'erase':
                // The role's curve runs the *disappearance*, so invert it to get
                // what's left on the board.
                return { angle: base, extent: 1 - value, done };
            case 'rotate':
                // `change` overshoots, so the mirror swings past square and settles.
                return { angle: base + tween.spin * (Math.PI / 2) * value, extent: 1, done };
        }
    }

    /**
     * One mirror, as the canonical `/` stroke rotated by `angle` about the cell
     * centre and grown to `extent` of its length from its starting end. At rest
     * this is exactly the inset diagonal of the cell.
     */
    private drawMirrorStroke(pos: Position, angle: number, extent: number, color: string): void {
        if (extent <= 0) return;
        const { cellSize } = this.config;
        const half = cellSize / 2 - cellSize * 0.15;   // inset from the cell corners
        const center = this.getCellCenter(pos);

        this.ctx.save();
        this.ctx.translate(center.x, center.y);
        this.ctx.rotate(angle);

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.mirrorsConfig.mirrorWidth;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(-half, half);
        this.ctx.lineTo(-half + 2 * half * extent, half - 2 * half * extent);
        this.ctx.stroke();

        this.ctx.restore();
    }

    // ── Warnings ──────────────────────────────────────────────────────

    protected drawWarnings(game: MirrorsGame): void {
        const path = game.traceLaser();
        const grid = game.getGrid();
        const digitPositions = grid.getAllDigitPositions();

        const warned = new Set<string>();

        const warn = (digit: number) => {
            const pos = digitPositions.get(digit);
            if (!pos) return;
            const key = `${pos.row},${pos.col}`;
            if (warned.has(key)) return;
            warned.add(key);
            this.drawCellBorder(pos, COLORS.red);
        };

        for (const digit of path.digitsCrossedTwice) {
            warn(digit);
        }

        let expected = grid.minDigit;
        for (const digit of path.digitsHit) {
            if (digit !== expected) {
                warn(digit);
            }
            expected++;
        }
    }

    // ── Digit Badges ──────────────────────────────────────────────────

    protected drawDigitBadges(
        game: MirrorsGame,
        mainAnalysis: BeamAnalysis,
        hintAnalysis: HintBeamAnalysis | null
    ): void {
        const grid = game.getGrid();
        const digitPositions = grid.getAllDigitPositions();

        // Collect hint beam valid digits (amber), then override with main beam (teal)
        const badgeColors = new Map<number, { color: string; filled: boolean }>();

        // Red empty badges for hint beam wrong-order digits (lowest priority)
        if (hintAnalysis) {
            for (const digit of hintAnalysis.invalidDigits) {
                badgeColors.set(digit, { color: COLORS.red, filled: false });
            }
        }

        if (hintAnalysis) {
            for (const digit of hintAnalysis.validDigits) {
                badgeColors.set(digit, { color: COLORS.amber, filled: true });
            }
        }

        // Main beam has priority
        for (const digit of mainAnalysis.validDigits) {
            badgeColors.set(digit, { color: COLORS.teal, filled: true });
        }

        for (const [digit, { color, filled }] of badgeColors) {
            const pos = digitPositions.get(digit);
            if (!pos) continue;
            this.drawBadge(pos, 'NE', '', color, 'xs', filled);
        }
    }
}
